<!--
    WorkIQ "Your working patterns" (PLT-1866), restyled onto the same anatomy as the insights card
    (PLT-2189) so the two WorkIQ sections read as a pair: section header with a "Powered by Work IQ"
    chip, then an InsightCardLayout with a meta line, serif headline, prose summary, separator, and a
    footer holding a secondary affordance on the left and the CTA on the right.

    Reuse only, same rule as the insights card: InsightCardLayout, InsightCategoryLabel and
    DiveDeeperButton are imported read-only and nothing under ~vue/Labs is modified.
-->
<template>
    <section data-testid="home-mywork-progress-card">
        <HomeSectionHeader :title="t('chat.home.workIq.progressHeading')">
            <template #actions>
                <SourceChip class="text-ink-muted">{{ t("chat.home.workIq.poweredBy") }}</SourceChip>
            </template>
        </HomeSectionHeader>

        <InsightCardLayout>
            <div class="mb-3 flex flex-wrap items-center gap-2">
                <InsightCategoryLabel :label="t('chat.home.workIq.weekTrend', { weeks: progress.weeks })" :tier="WORKIQ_TIER" />
                <span class="scaffold-details-small text-ink-muted">{{ t("chat.home.workIq.periodRange", { range: progress.period_range_label }) }}</span>
            </div>

            <div class="mb-3 flex min-h-14 items-center">
                <h4 class="display-small-serif text-ink">{{ headline }}</h4>
            </div>

            <p class="body-small text-ink-muted">{{ progress.headline.summary }}</p>

            <ul class="mt-4 flex flex-col gap-2">
                <li v-for="metric in progress.metrics" :key="metric.metric" class="flex items-center justify-between gap-3">
                    <span class="body-small text-ink">{{ metric.label }}</span>
                    <span class="body-small-semibold" :class="trendClass(metric.trend)">
                        {{ formatValue(metric.recent) }}
                        <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -- punctuation around a formatted number -->
                        <span v-if="metric.pct_change !== null">({{ formatPct(metric.pct_change) }})</span>
                    </span>
                </li>
            </ul>

            <HorizontalDivider as="hr" spacing="none" class="mt-4" />

            <div class="mt-3 flex items-center justify-between gap-3">
                <BaseDialog
                    :title="t('chat.home.workIq.trendsDialogTitle', { weeks: progress.weeks })"
                    :description="t('chat.home.workIq.trendsDialogDescription', { headline: headline.toLowerCase() })">
                    <template #trigger>
                        <BaseButton type="button" theme="tertiary" size="sm" class="inline-flex items-center gap-2" data-testid="home-mywork-progress-detail-trigger">
                            <i class="bi bi-graph-up text-[11px]/none" aria-hidden="true"></i>
                            <span>{{ t("chat.home.workIq.seeTrends", { weeks: progress.weeks }) }}</span>
                        </BaseButton>
                    </template>
                    <HomeMyWorkTrendsDetail :progress="progress" :headline="progress.headline" />
                </BaseDialog>

                <DiveDeeperButton data-testid="home-mywork-progress-start" :loading="starting" @click="startChat" />
            </div>
            <span v-if="error" role="alert" class="details mt-1 text-status-error">{{ error }}</span>
        </InsightCardLayout>
    </section>
</template>

<script setup lang="ts">
import { useFetch } from "~vue/api";
import HomeSectionHeader from "~vue/ChatHome/HomeSectionHeader.vue";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseDialog from "~vue/components/BaseDialog.vue";
import HorizontalDivider from "~vue/components/HorizontalDivider.vue";
import InsightCardLayout from "~vue/components/InsightCardLayout.vue";
import SourceChip from "~vue/components/SourceChip.vue";
import DiveDeeperButton from "~vue/Labs/DiveDeeperButton.vue";
import type { InsightTier } from "~vue/Labs/insightCategory";
import InsightCategoryLabel from "~vue/Labs/InsightCategoryLabel.vue";
import type { MyWorkProgressData } from "~vue/types/chat_home";
import { openUrl } from "~vue/utils";
import { formatNumber } from "~vue/utils/formatNumber";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

import HomeMyWorkTrendsDetail from "./HomeMyWorkTrendsDetail.vue";

const props = withDefaults(
    defineProps<{
        progress: MyWorkProgressData;
        startUrl?: string;
    }>(),
    { startUrl: "/workiq/api/progress-chat/" },
);

const { t } = useI18n();

// Tier 4 is already "Work IQ" in the existing tier system, so both WorkIQ cards get the same dot.
const WORKIQ_TIER: InsightTier = 4;

const starting = ref(false);
const error = ref<string | null>(null);

const headline = computed(() => {
    const { label, pct_change, phrase } = props.progress.headline;
    // The server only supplies a phrase for a metric whose direction it is willing to characterize;
    // everything else falls back to a bare, verdict-free statement of the movement.
    if (phrase) {
        return phrase;
    }
    if (pct_change === null) {
        return t("chat.home.workIq.headlineShifting", { label });
    }
    const pct = formatNumber(Math.abs(roundToWholePercent(pct_change)), "percent");
    return pct_change < 0 ? t("chat.home.workIq.headlineDown", { label, pct }) : t("chat.home.workIq.headlineUp", { label, pct });
});

function formatValue(value: number | null): string {
    if (value === null) {
        return "—";
    }
    return formatNumber(value, "up-to-one-decimal");
}

// Math.round first so -12.5% stays -12% as it always was; Intl alone rounds it away from zero to -13%.
function roundToWholePercent(pct: number): number {
    return Math.round(pct * 100) / 100;
}

function formatPct(pct: number): string {
    return formatNumber(roundToWholePercent(pct), "signed-percent");
}

// "changed" is a contextual metric: it moved, and we are not claiming the direction is good or bad, so
// it stays neutral rather than borrowing the green/red verdict colours.
function trendClass(trend: string): string {
    if (trend === "improving") {
        return "text-chip-status-done-text";
    }
    if (trend === "declining") {
        return "text-chip-status-overdue-text";
    }
    return "text-ink-muted";
}

async function startChat() {
    if (starting.value) {
        return;
    }
    starting.value = true;
    error.value = null;
    try {
        const { data, response } = await useFetch(props.startUrl).post({}).json<{ coaching_session_url?: string; error?: string }>();
        if (response.value?.ok && data.value?.coaching_session_url) {
            openUrl(data.value.coaching_session_url);
        } else {
            error.value = data.value?.error || t("chat.home.workIq.startChatError");
        }
    } finally {
        starting.value = false;
    }
}
</script>
